<template>
  <div ref="chartContainer" class="positions-data-chart">
    <VChart :option="chartOptions" autoresize @click="onChartClick" @datazoom="onDataZoom" />
    <div v-if="isDropdownVisible" class="options-dropdown" :style="dropdownStyle" @click.stop>
      <button
          v-for="option in positionClickOptions"
          :key="option.key"
          class="options-dropdown-item"
          @click="onOptionClick(option.key)">
        {{ $t(option.value) }}
      </button>
    </div>
  </div>
</template>

<script>
import VChart from "vue-echarts";
import moment from "moment/moment";

import {use} from 'echarts/core';
import {LineChart} from 'echarts/charts';
import {
  DataZoomComponent,
  GridComponent,
  LegendComponent,
  MarkPointComponent,
  ToolboxComponent,
  TooltipComponent,
  MarkAreaComponent
} from 'echarts/components';
import {CanvasRenderer} from 'echarts/renderers';

const FIVE_MINUTES_IN_MILLISECONDS = 5 * 60 * 1000;
const TEN_MINUTES_IN_MILLISECONDS = 10 * 60 * 1000;
const QUARTER_HOUR_IN_MILLISECONDS = 15 * 60 * 1000;
const HALF_HOUR_IN_MILLISECONDS = 30 * 60 * 1000;
const HOUR_IN_MILLISECONDS = 60 * 60 * 1000;
const FIVE_MINUTE_INTERVAL_MAX_RANGE = HOUR_IN_MILLISECONDS;
const TEN_MINUTE_INTERVAL_MAX_RANGE = 3 * HOUR_IN_MILLISECONDS;
const QUARTER_HOUR_INTERVAL_MAX_RANGE = 6 * HOUR_IN_MILLISECONDS;
const HALF_HOUR_INTERVAL_MAX_RANGE = 10 * HOUR_IN_MILLISECONDS;

use([CanvasRenderer,
  ToolboxComponent,
  LineChart,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  DataZoomComponent,
  MarkAreaComponent,
  MarkPointComponent])

export default {
  name: 'PositionsDataChart',
  components: {VChart},
  emits: ['position-clicked'],
  props: {
    positions: {
      type: Array,
      required: true,
    },
    selectedProps: {
      type: Array,
      required: true,
    },
    propThresholds: {
      type: Object,
      default() {
        return {};
      }
    },
    fileDownloadName: {
      type: String,
      default: 'VehicleProperties'
    },
    positionClickOptions: {
      type: Array,
      default() {
        return [];
      }
    }
  },
  data() {
    return {
      isDropdownVisible: false,
      selectedPosition: null,
      selectedSeriesAttributeName: null,
      dropdownX: 0,
      dropdownY: 0,
      dataZoomStart: 0,
      dataZoomEnd: 100,
    };
  },
  mounted() {
    document.addEventListener('click', this.onDocumentClick);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.onDocumentClick);
  },
  watch: {
    positions() {
      this.dataZoomStart = 0;
      this.dataZoomEnd = 100;
    }
  },
  computed: {
    dropdownStyle() {
      return {
        left: `${this.dropdownX}px`,
        top: `${this.dropdownY}px`,
      };
    },
    yAxes() {
      return this.propsSeries.map(item => this.buildYAxis(item.data));
    },
    xAxisInterval() {
      if (this.positions.length < 2) {
        return HOUR_IN_MILLISECONDS;
      }

      const fullRange = this.positions[this.positions.length - 1].time.valueOf()
          - this.positions[0].time.valueOf();
      const visibleRange = fullRange * Math.abs(this.dataZoomEnd - this.dataZoomStart) / 100;

      switch (true) {
        case visibleRange < FIVE_MINUTE_INTERVAL_MAX_RANGE:
          return FIVE_MINUTES_IN_MILLISECONDS;
        case visibleRange < TEN_MINUTE_INTERVAL_MAX_RANGE:
          return TEN_MINUTES_IN_MILLISECONDS;
        case visibleRange < QUARTER_HOUR_INTERVAL_MAX_RANGE:
          return QUARTER_HOUR_IN_MILLISECONDS;
        case visibleRange <= HALF_HOUR_INTERVAL_MAX_RANGE:
          return HALF_HOUR_IN_MILLISECONDS;
        default:
          return HOUR_IN_MILLISECONDS;
      }
    },
    chartOptions() {
      return {
        toolbox: {
          feature: {
            saveAsImage: {
              name: this.fileDownloadName
            }
          }
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            label: {
              formatter: params => moment(params.value).format('HH:mm')
            }
          }
        },
        legend: {
          top: 20,
          right: 50,
          left: 'center',
          data: this.positions?.length > 0 ? this.propsSeries.map(item => this.$t(item.prop.descriptionKey)) : []
        },
        grid: {
          left: '0',
          right: '0',
          bottom: '55',
            containLabel: true
        },
        dataZoom: [
          {
            type: 'inside',
            start: this.dataZoomStart,
            end: this.dataZoomEnd
          },
          {
            start: this.dataZoomStart,
            end: this.dataZoomEnd,
            labelFormatter: value => moment(value).format('HH:mm')
          }
        ],
        yAxis: this.yAxes,
        xAxis: {
          type: 'value',
          scale: true,
          interval: this.xAxisInterval,
          min: ({min}) => Math.floor(min / this.xAxisInterval) * this.xAxisInterval,
          max: ({max}) => Math.ceil(max / this.xAxisInterval) * this.xAxisInterval,
          axisLine: { onZero: false },
          axisLabel: {
            formatter: value => moment(value).format('HH:mm')
          }
        },
        series: this.propsSeries
            .map((item, seriesIndex) => {
              const result = {
                name: this.$t(item.prop.descriptionKey),
                type: 'line',
                smooth: true,
                data: item.data.map((value, positionIndex) => ({
                  value: [this.positions[positionIndex].time.valueOf(), value],
                  positionIndex
                })),
                yAxisIndex: seriesIndex,
              };

              if (item.prop.tooltipFormatFn) {
                result.tooltip = {
                  valueFormatter: item.prop.tooltipFormatFn
                }
              }

              if (item.markAreaData) {
                result.showSymbol = false;
                result.lineStyle = { width: 0 };
                result.itemStyle = {color: item.prop.markAreaColor};
                result.markArea = {
                  silent: false,
                  label: {show: false},
                  emphasis: {
                    label: {show: true}
                  },
                  itemStyle: {color: item.prop.markAreaColor},
                  data: item.markAreaData.map(x => {
                    return [
                      {
                        name: result.name,
                        xAxis: this.positions[x.startIndex].time.valueOf()
                      },
                      {
                        xAxis: this.positions[x.endIndex].time.valueOf()
                      }
                    ]
                  })
                };
              }

              if (item.prop.showAsMarkArea !== true) {
                result.markPoint = {
                  symbol: 'circle',
                  symbolSize: 18,
                  label: {
                    show: false
                  },
                  data: item.outOfRangePoints ?? [],
                };
              }

              return result;
            })
      }
    },
    propsSeries() {
      return this.selectedProps
          .map(prop => {
            const result = {prop};
            if (prop.showAsMarkArea) {
              result.markAreaData = this.getMarkAreaRanges(this.positions, prop.key);
            }

            result.data = this.positions.map(pos => this.getChartValue(pos, prop));
            result.outOfRangePoints = this.getOutOfRangePoints(this.positions, prop);

            return result;
          })
          .filter(item => item.data?.some(x => x != null) || item.markAreaData?.length > 0 || item.outOfRangePoints?.length > 0);
    }
  },
  methods: {
    onDataZoom(params) {
      const zoom = params?.batch?.[0] ?? params;
      if (!Number.isFinite(zoom?.start) || !Number.isFinite(zoom?.end)) {
        return;
      }

      this.dataZoomStart = zoom.start;
      this.dataZoomEnd = zoom.end;
    },
    onDocumentClick(event) {
      if (!this.isDropdownVisible) {
        return;
      }

      if (this.$refs.chartContainer?.contains(event.target)) {
        return;
      }

      this.hideOptionsDropdown();
    },
    onChartClick(params) {
      if (!Array.isArray(this.positionClickOptions) || this.positionClickOptions.length === 0) {
        return;
      }

      if (params?.componentType !== 'series' && params?.componentType !== 'markPoint') {
        this.hideOptionsDropdown();
        return;
      }

      const seriesData = this.propsSeries?.[params.seriesIndex];
      const seriesAttributeName = seriesData?.prop?.key;
      if (!seriesAttributeName) {
        this.hideOptionsDropdown();
        return;
      }

      const position = this.positions?.[params?.data?.positionIndex];
      if (!position) {
        this.hideOptionsDropdown();
        return;
      }

      this.selectedPosition = position;
      this.selectedSeriesAttributeName = seriesAttributeName;
      this.dropdownX = params?.event?.event?.offsetX || 0;
      this.dropdownY = (params?.event?.event?.offsetY || 0) + 10;
      this.isDropdownVisible = true;
    },
    onOptionClick(optionKey) {
      this.$emit('position-clicked', optionKey, this.selectedPosition, this.selectedSeriesAttributeName);
      this.hideOptionsDropdown();
    },
    hideOptionsDropdown() {
      this.isDropdownVisible = false;
    },
    buildYAxis(data) {
      const numericValues = data
          .map(value => this.parseNumericValue(value))
          .filter(value => value != null);

      if (numericValues.length === 0) {
        return {
          type: 'value',
          show: false,
          scale: true,
        };
      }

      const minValue = Math.min(...numericValues);
      const maxValue = Math.max(...numericValues);
      const range = maxValue - minValue;

      if (range === 0) {
        const basePadding = Math.max(Math.abs(maxValue) * 0.1, 1);
        return {
          type: 'value',
          show: false,
          scale: true,
          min: minValue - basePadding,
          max: maxValue + basePadding,
        };
      }

      const padding = range * 0.2;

      return {
        type: 'value',
        show: false,
        scale: true,
        min: minValue - padding,
        max: maxValue + padding,
      };
    },
    getChartValue(position, prop) {
      if (prop.yAxisFormatFn) {
        return prop.yAxisFormatFn(position[prop.key]);
      }

      return position[prop.key];
    },
    parseNumericValue(value) {
      if (value === '' || value == null) {
        return null;
      }

      const parsedValue = typeof value === 'number' ? value : parseFloat(value);
      return Number.isFinite(parsedValue) ? parsedValue : null;
    },
    getOutOfRangePoints(positions, prop) {
      const threshold = this.propThresholds?.[prop.key];
      const min = this.parseNumericValue(threshold?.min);
      const max = this.parseNumericValue(threshold?.max);
      if (min == null && max == null) {
        return [];
      }

      return positions.reduce((result, position, index) => {
        const chartValue = this.getChartValue(position, prop);
        const numericValue = this.parseNumericValue(chartValue);
        if (numericValue == null) {
          return result;
        }

        const isBelowMin = min != null && numericValue < min;
        const isAboveMax = max != null && numericValue > max;
        if (!isBelowMin && !isAboveMax) {
          return result;
        }

        result.push({
          xAxis: position.time.valueOf(),
          yAxis: numericValue,
          value: numericValue,
          positionIndex: index,
          itemStyle: {
            color: isBelowMin ? '#F59E0B' : '#D32F2F',
            borderColor: '#FFFFFF',
            borderWidth: 2,
          }
        });

        return result;
      }, []);
    },
    getMarkAreaRanges(positions, prop) {
      const result = [];
      let currentStart = null;

      for (let i = 0; i < positions.length; i++) {
        const propValue = positions[i][prop];

        if (propValue && currentStart === null) {
          // start of a new true block
          currentStart = i;
        }

        if (currentStart !== null && (!propValue || i === positions.length - 1)) {
          // end of the current true block
          const endIndex =
              propValue && i === positions.length - 1
                  ? i
                  : i - 1;

          result.push({
            startIndex: currentStart,
            endIndex,
          });

          currentStart = null;
        }
      }

      return result;
    }

  }
}
</script>

<style scoped lang="scss">
.positions-data-chart {
  position: relative;
}

.options-dropdown {
  position: absolute;
  z-index: 10;
  display: flex;
  flex-direction: column;
  min-width: 180px;
  background: #ffffff;
  border: 1px solid #d0d7de;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.15);
}

.options-dropdown-item {
  border: 0;
  background: #ffffff;
  color: #000000;
  text-align: left;
  padding: 8px 10px;
  cursor: pointer;
}

.options-dropdown-item:hover {
  background: #f4f6f8;
}
</style>
